'use strict'
import { useMemo, useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel
} from '@material-ui/core'

const COLUMNS = [
  { id: 'propertyName', label: 'Property' },
  { id: 'fromCrewName', label: 'Current Crew' },
  { id: 'result', label: 'Result' },
  { id: 'reason', label: 'Reason' },
  { id: 'distanceMiles', label: 'Distance (mi)' },
  { id: 'warnings', label: 'Warning' }
]

export default function CutCrewPreviewTable ({ suggestedMoves, unassigned }) {
  const [orderBy, setOrderBy] = useState('result')
  const [order, setOrder] = useState('asc')

  const rows = useMemo(() => {
    const moveRows = (suggestedMoves || []).map(m => ({
      ...m,
      result: m.toCrewName || 'Reassigned',
      resultType: 'reassigned'
    }))
    const unassignedRows = (unassigned || []).map(u => ({
      ...u,
      result: 'Unassigned',
      resultType: 'unassigned',
      distanceMiles: null,
      score: null
    }))
    return [...unassignedRows, ...moveRows]
  }, [suggestedMoves, unassigned])

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      if (orderBy === 'result') {
        const aVal = a.resultType === 'unassigned' ? 0 : 1
        const bVal = b.resultType === 'unassigned' ? 0 : 1
        return order === 'asc' ? aVal - bVal : bVal - aVal
      }
      const aVal = a[orderBy] ?? ''
      const bVal = b[orderBy] ?? ''
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return order === 'asc' ? aVal - bVal : bVal - aVal
      }
      return order === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal))
    })
  }, [rows, orderBy, order])

  const handleSort = (columnId) => {
    if (orderBy === columnId) {
      setOrder(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setOrderBy(columnId)
      setOrder('asc')
    }
  }

  return (
    <TableContainer style={{ maxHeight: 400, overflow: 'auto', borderRadius: 8, border: '1px solid var(--df-line, #e0d8c2)' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {COLUMNS.map(col => (
              <TableCell key={col.id} style={{
                background: 'var(--df-surface-2, #f4efe4)',
                fontWeight: 700, fontSize: 11, color: 'var(--df-ink-3)',
                textTransform: 'uppercase', letterSpacing: '0.04em'
              }}>
                <TableSortLabel
                  active={orderBy === col.id}
                  direction={orderBy === col.id ? order : 'asc'}
                  onClick={() => handleSort(col.id)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row, idx) => (
            <TableRow key={row.aspirePropertyId || idx} style={{
              borderLeft: row.resultType === 'unassigned'
                ? '3px solid #a9341d'
                : '3px solid #2f7a3e'
            }}>
              <TableCell style={{ fontSize: 12, fontWeight: 600 }}>{row.propertyName}</TableCell>
              <TableCell style={{ fontSize: 12 }}>{row.fromCrewName}</TableCell>
              <TableCell style={{
                fontSize: 12, fontWeight: 700,
                color: row.resultType === 'reassigned' ? '#2f7a3e' : '#a9341d'
              }}>
                {row.result}
              </TableCell>
              <TableCell style={{ fontSize: 12, color: 'var(--df-ink-3)' }}>{(row.reason || '').replace(/_/g, ' ')}</TableCell>
              <TableCell style={{ fontSize: 12 }}>{row.distanceMiles != null ? row.distanceMiles : '—'}</TableCell>
              <TableCell>
                {(row.warnings || []).map((w, i) => (
                  <span key={i} className="rw-pill" style={{
                    background: '#fbe8d6', color: '#a4502f',
                    marginRight: 4, fontSize: 10
                  }}>
                    {w}
                  </span>
                ))}
              </TableCell>
            </TableRow>
          ))}
          {sorted.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center" style={{ padding: 24, color: 'var(--df-ink-4)' }}>
                No displaced properties
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
